import {
  AutoComplete,
  Select as AntSelect,
  type SelectProps as AntSelectProps,
  type TreeSelectProps,
  Tooltip,
} from "antd";
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useState,
} from "react";
import Input, { type InputProps, InputType } from "./input";
import useTranslation from "utils/hooks/useTranslation";

type OptGroupType = { isGroup: true; text: string };

export type SelectProps = InputProps &
  Omit<
    AntSelectProps<any, Record<string | number, string>>,
    keyof InputProps | "options"
  > & {
    optionRender?: React.FC<
      PropsWithChildren<{
        value?: string | number;
        text?: string | number;
        icon?: string;
        disabledState?: { disabled?: boolean };
      }>
    >;
    options: (
      | string
      | number
      | Record<string | number, string | number>
      | OptGroupType
    )[];
    searchOptions?: {
      caseSensitive?: boolean;
      noAccent: boolean;
      contains: boolean;
    };
    allowSearch?: boolean;
    addEmptyOption?: boolean;
    autocomplete?: boolean;
    autoSelectSingleOption?: boolean;
  };

export const SelectInner: React.FC<SelectProps> = forwardRef<
  ReactElement,
  PropsWithChildren<SelectProps>
>(
  (
    {
      options = [],
      autocomplete = false,
      searchOptions = { caseSensitive: false, contains: true, noAccent: true },
      allowSearch = false,
      allowClear = false,
      addEmptyOption = false,
      autoSelectSingleOption,
      fixLength,
      onClear,
      optionRender: OptionRender,
      ...rest
    },
    ref
  ) => {
    const [isMount, setIsMount] = useState(false);
    const { t } = useTranslation();
    const { Option } = AntSelect;
    const formattedOptions = (options || []).map((o: any) => {
      const isPlainObject = typeof o !== "object";
      let value: string | number;
      let text: string | number;
      const data: any = o?.data!;
      let icon: string = "";
      let disableState = {};

      if (isPlainObject) {
        value = text = o as any;
      } else if (o.isGroup) {
        text = o.text;
        value = "";
        disableState = { disabled: true };
      } else {
        const option: Record<string | number, string> = o as any;
        value = option.value;
        text =
          typeof option.text === "undefined"
            ? value
            : rest.translateValue
            ? t(option.text)
            : option.text;
        icon = option.icon;
        disableState = { disabled: option.disabled };
      }
      if (autocomplete && !(o as any).isGroup) {
        value = String(value);
        text = String(text);
      }
      return { value, text, data, disableState, icon };
    });

    const filterOption = (
      inputText: string,
      o?: number | string | Record<string | number, string>
    ) => {
      let text: string;
      const isPlainObject = typeof o !== "object";
      if (isPlainObject) {
        text = o?.toString() || "";
      } else {
        const option: Record<string | number, string> = o as any;
        text = option?.text || option?.value || "";
      }
      if (!searchOptions.caseSensitive) {
        text = text.toLowerCase();
      }

      let searchInput = searchOptions.caseSensitive
        ? inputText
        : inputText?.toLowerCase();
      if (searchOptions.noAccent) {
        text = text?.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        searchInput =
          searchInput?.normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
      }
      return (
        !searchInput ||
        (text &&
          (searchOptions.contains
            ? text.includes(searchInput)
            : text.startsWith(searchInput))) ||
        false
      );
    };
    let onClearValue = onClear;
    const { onChange, value, onCustomChange, name } = rest;
    if (allowClear && !onClearValue) {
      onClearValue = () => {
        onChange?.(emptyVal as any);
        onCustomChange?.(name as string, emptyVal as any);
      };
    }

    useEffect(() => {
      if (isMount && autoSelectSingleOption && formattedOptions.length === 1) {
        if (formattedOptions[0].value !== value) {
          onChange?.(formattedOptions[0].value as any);
          onCustomChange?.(name as string, formattedOptions[0].value as any);
        }
      }
    }, [formattedOptions, onChange, isMount, autoSelectSingleOption]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      setIsMount(true);
    }, []);

    const emptyVal = rest.multiple ? [] : null;
    const Component = autocomplete ? AutoComplete : AntSelect;

    return (
      <Component
        id={name}
        ref={ref}
        filterOption={filterOption}
        showSearch={allowSearch}
        allowClear={allowClear && !!value}
        onClear={onClearValue}
        optionLabelProp="text"
        notFoundContent={<></>}
        {...(fixLength ? { fixlength: fixLength } : null)}
        {...(rest as any)}
        onChange={(v) => {
          onChange?.(v === undefined ? emptyVal : v);
          onCustomChange?.(name as string, v === undefined ? emptyVal : v);
        }}
      >
        {allowClear && (
          <Option value="" style={addEmptyOption ? null : { display: "none" }}>
            {" "}
          </Option>
        )}
        {formattedOptions?.map((o) => {
          const { value, text, icon, disableState } = o;
          return (
            <Option
              key={`${value}`}
              value={value}
              text={text}
              {...disableState}
            >
              {OptionRender ? (
                <OptionRender {...o} />
              ) : (
                <group
                  data-interactive=""
                  data-radius="3"
                  data-space="10"
                  data-wrap="no"
                  // data-gap="5"
                  data-align="center"
                  data-height="30"
                >
                  {icon ? (
                    <>
                      <icon>
                        <img src={icon} alt="" />
                      </icon>
                      {text && (
                        <Tooltip arrow={false} title={text}>
                          <text data-ellipsis="" data-space="5">
                            {text}
                          </text>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <Tooltip arrow={false} title={text}>
                      <text data-ellipsis="">{text || " "} </text>
                    </Tooltip>
                  )}
                </group>
              )}
            </Option>
          );
        })}
      </Component>
    );
  }
);

const Select: React.FC<SelectProps> = forwardRef<
  ReactElement,
  PropsWithChildren<SelectProps>
>((props, ref) => {
  return <Input ref={ref} {...props} type={InputType.Select} />;
});

export const TreeSelect: React.FC<InputProps & TreeSelectProps> = forwardRef<
  ReactElement,
  PropsWithChildren<InputProps & TreeSelectProps>
>((props, ref) => {
  return <Input ref={ref} {...props} type={InputType.TreeSelect} />;
});
export default Select;
